import { Alert, Button, Group, Paper, Stack, TextInput } from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { useTranslation } from "react-i18next"
import { useResetPassword } from "../hooks/useAgencyUsers"
import { getErrorMessage } from "../utils"
import type { ResetPasswordFormValues } from "../models/agencyUser"

interface Props {
  userId: number
  username: string
  onSuccess: () => void
}

export default function ResetPasswordFormContent({
  userId,
  username,
  onSuccess,
}: Props) {
  const { t } = useTranslation()
  const resetMutation = useResetPassword()

  const form = useForm<ResetPasswordFormValues>({
    initialValues: { password: "" },
    validate: {
      password: (v) =>
        v?.length >= 6 ? null : t("agencyUsers.validation.passwordMin"),
    },
  })

  const handleSubmit = (values: ResetPasswordFormValues) => {
    resetMutation.mutate(
      { id: userId, password: values.password },
      {
        onSuccess: () => {
          notifications.show({
            color: "green",
            title: t("common.success"),
            message: t("agencyUsers.resetPasswordSuccess"),
          })
          onSuccess()
        },
        onError: (err) => {
          notifications.show({
            color: "red",
            title: t("common.error"),
            message: getErrorMessage(err),
          })
        },
      },
    )
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      {resetMutation.isError && (
        <Alert
          color="red"
          mb="md"
          withCloseButton
          onClose={() => resetMutation.reset()}
        >
          {resetMutation.error?.message}
        </Alert>
      )}
      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          <TextInput
            label={t("agencyUsers.username")}
            value={username}
            disabled
          />
          <TextInput
            label={t("agencyUsers.password")}
            type="password"
            autoComplete="new-password"
            {...form.getInputProps("password")}
          />
        </Stack>
      </Paper>

      <Group justify="flex-end" mt="lg">
        <Button variant="default" onClick={onSuccess}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" loading={resetMutation.isPending}>
          {t("agencyUsers.resetPassword")}
        </Button>
      </Group>
    </form>
  )
}
